// Screens API — extracted from api.js (Tier 2 #9a round 2).
// All screen variants (regular, DV, RV, AV, chart), the global screen
// template library, screen permissions, header configs, and the activity-
// scoped form-screen + grid-layout helpers live here.

import api from './api';
import { resolveAppId } from './appScope';

// ─── Screens ───────────────────────────────────────────────────────────────
export const fetchScreensByApp = async (appId, device, { signal } = {}) => {
  const params = device ? `?device=${device}` : '';
  const response = await api.get(`/apps/${appId}/screens${params}`, { signal });
  return response.data;
};

export const createScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/screens`, data);
  return response.data;
};

// AI: generate/edit an HTML-mode screen body → { html_content }.
// Pass currentHtml to EDIT the existing screen (the model returns the full
// updated HTML); omit it to generate from scratch. Proxies to the LLM gateway.
export const generateHtmlScreen = async (appId, prompt, currentHtml = "") => {
  const response = await api.post(`/apps/${appId}/generate-html-screen`, { prompt, current_html: currentHtml });
  return response.data;
};

export const cloneScreen = async (appId, screenUuid) => {
  const response = await api.post(`/apps/${appId}/screens/${screenUuid}/clone`);
  return response.data;
};

// ─── Detail View Screens ───────────────────────────────────────────────────
export const fetchDvScreensByApp = async (appId, device, { signal } = {}) => {
  const params = device ? `?device=${device}` : '';
  const response = await api.get(`/apps/${appId}/dv-screens${params}`, { signal });
  return response.data;
};

export const createDvScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/dv-screens`, data);
  return response.data;
};

export const cloneDvScreen = async (appId, dvScreenUuid) => {
  const response = await api.post(`/apps/${appId}/dv-screens/${dvScreenUuid}/clone`);
  return response.data;
};


// ─── Record View Screens ───────────────────────────────────────────────────
export const fetchRvScreensByApp = async (appId, device, { signal } = {}) => {
  const params = device ? `?device=${device}` : '';
  const response = await api.get(`/apps/${appId}/rv-screens${params}`, { signal });
  return response.data;
};

export const createRvScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/rv-screens`, data);
  return response.data;
};

export const cloneRvScreen = async (appId, rvScreenUuid) => {
  const response = await api.post(`/apps/${appId}/rv-screens/${rvScreenUuid}/clone`);
  return response.data;
};

export const updateRvScreen = async (rvScreenId, data) => {
  const response = await api.put(`/rv-screens/${rvScreenId}`, data);
  return response.data;
};

export const deleteRvScreen = async (rvScreenId, options = {}) => {
  const response = await api.delete(`/rv-screens/${rvScreenId}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

export const fetchRvScreen = async (rvScreenId) => {
  const response = await api.get(`/rv-screens/${rvScreenId}`);
  return response.data;
};

export const fetchDvScreen = async (dvScreenId) => {
  const response = await api.get(`/dv-screens/${dvScreenId}`);
  return response.data;
};

// ─── Screen Templates (global library, shared across all positioned editors) ─
// Filter by `screen_type` + `device`. Server includes 'universal' templates
// alongside the requested type.
export const fetchScreenTemplates = async ({ screen_type, device, kind } = {}, { signal } = {}) => {
  const params = {};
  if (screen_type) params.screen_type = screen_type;
  if (device) params.device = device;
  if (kind) params.kind = kind;
  const response = await api.get(`/screen-templates`, { params, signal });
  return response.data;
};

export const fetchScreenTemplate = async (templateId, { signal } = {}) => {
  const response = await api.get(`/screen-templates/${templateId}`, { signal });
  return response.data;
};

// Save a new template to the global gallery. Body must include name + screen_type + layout.
// Optional: description, category, device, template_kind, preview_image_url.
export const createScreenTemplate = async (payload) => {
  const response = await api.post(`/screen-templates`, payload);
  return response.data;
};

export const deleteScreenTemplate = async (templateId) => {
  const response = await api.delete(`/screen-templates/${templateId}`);
  return response.data;
};

// ─── Activity View Screens ───────────────────────────────────────────────────
export const fetchAvScreensByApp = async (appId, device, options = {}) => {
  let params = device ? `?device=${device}` : '';
  if (options.workflowId != null) {
    const sep = device ? '&' : '?';
    params += `${sep}workflow_id=${encodeURIComponent(options.workflowId)}`;
  }
  const response = await api.get(`/apps/${appId}/av-screens${params}`, { signal: options.signal });
  return response.data;
};

export const createAvScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/av-screens`, data);
  return response.data;
};

export const cloneAvScreen = async (appId, avScreenUuid) => {
  const response = await api.post(`/apps/${appId}/av-screens/${avScreenUuid}/clone`);
  return response.data;
};

// ── Phase Z.2: app-scoped uuid screen helpers ───────────────────────────────
// New helpers that target the uuid-keyed routes. Coexist with the legacy
// integer-PK helpers above. Use these for any "open in editor from JSONB
// reference" deep-link, since the JSONB carries a uuid (not a PK). The
// existing helpers stay around so legacy callers don't break — they get
// migrated incrementally, then the legacy versions are removed in Z.4.

export const fetchScreenByUUID = async (appId, screenUuid) => {
  const response = await api.get(`/apps/${appId}/screens/${screenUuid}`);
  return response.data;
};
export const updateScreenByUUID = async (appId, screenUuid, data) => {
  const response = await api.put(`/apps/${appId}/screens/${screenUuid}`, data);
  return response.data;
};
export const deleteScreenByUUID = async (appId, screenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/screens/${screenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};
export const fetchScreenPermissionsByUUID = async (appId, screenUuid) => {
  const response = await api.get(`/apps/${appId}/screens/${screenUuid}/permissions`);
  return response.data;
};
export const updateScreenPermissionsByUUID = async (appId, screenUuid, data) => {
  const response = await api.put(`/apps/${appId}/screens/${screenUuid}/permissions`, data);
  return response.data;
};

export const fetchDvScreenByUUID = async (appId, dvScreenUuid) => {
  const response = await api.get(`/apps/${appId}/dv-screens/${dvScreenUuid}`);
  return response.data;
};
export const updateDvScreenByUUID = async (appId, dvScreenUuid, data) => {
  const response = await api.put(`/apps/${appId}/dv-screens/${dvScreenUuid}`, data);
  return response.data;
};
export const deleteDvScreenByUUID = async (appId, dvScreenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/dv-screens/${dvScreenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

export const fetchRvScreenByUUID = async (appId, rvScreenUuid) => {
  const response = await api.get(`/apps/${appId}/rv-screens/${rvScreenUuid}`);
  return response.data;
};
export const updateRvScreenByUUID = async (appId, rvScreenUuid, data) => {
  const response = await api.put(`/apps/${appId}/rv-screens/${rvScreenUuid}`, data);
  return response.data;
};
export const deleteRvScreenByUUID = async (appId, rvScreenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/rv-screens/${rvScreenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

export const fetchAvScreenByUUID = async (appId, avScreenUuid) => {
  const response = await api.get(`/apps/${appId}/av-screens/${avScreenUuid}`);
  return response.data;
};
export const updateAvScreenByUUID = async (appId, avScreenUuid, data) => {
  const response = await api.put(`/apps/${appId}/av-screens/${avScreenUuid}`, data);
  return response.data;
};
export const deleteAvScreenByUUID = async (appId, avScreenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/av-screens/${avScreenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

export const fetchChartScreenByUUID = async (appId, chartScreenUuid) => {
  const response = await api.get(`/apps/${appId}/chart-screens/${chartScreenUuid}`);
  return response.data;
};
export const updateChartScreenByUUID = async (appId, chartScreenUuid, data) => {
  const response = await api.put(`/apps/${appId}/chart-screens/${chartScreenUuid}`, data);
  return response.data;
};
export const deleteChartScreenByUUID = async (appId, chartScreenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/chart-screens/${chartScreenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

// ─── MC Screens (RDBMS CRUD module) ──────────────────────────────────────────
// MC ("maker-checker" CRUD console) screens bind to an RDBMS CRUD template
// instead of a record view. The contract mirrors rv-screens / chart-screens
// exactly so the editor + nav-tree wiring is a straight reuse: the screen
// references its template by integer `mc_template_id` (RDBMSCrudTemplate.id),
// just as chart screens use `ct_template_id`.
export const fetchMcScreensByApp = async (appId, device, { signal } = {}) => {
  const params = device ? `?device=${device}` : '';
  const response = await api.get(`/apps/${appId}/mc-screens${params}`, { signal });
  return response.data;
};

export const createMcScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/mc-screens`, data);
  return response.data;
};

export const fetchMcScreenByUUID = async (appId, mcScreenUuid) => {
  const response = await api.get(`/apps/${appId}/mc-screens/${mcScreenUuid}`);
  return response.data;
};

export const updateMcScreenByUUID = async (appId, mcScreenUuid, data) => {
  const response = await api.put(`/apps/${appId}/mc-screens/${mcScreenUuid}`, data);
  return response.data;
};

export const deleteMcScreenByUUID = async (appId, mcScreenUuid, options = {}) => {
  const response = await api.delete(`/apps/${appId}/mc-screens/${mcScreenUuid}`, {
    params: options.force ? { force: "true" } : undefined,
  });
  return response.data;
};

// ─── Header Configs ────────────────────────────────────────────────────────
export const fetchHeaderConfigsByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/header-configs`, { signal });
  return response.data;
};

export const fetchHeaderConfigByDevice = async (appId, deviceType, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/header-configs/${deviceType}`, { signal });
  return response.data;
};

export const createHeaderConfig = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/header-configs`, data);
  return response.data;
};

export const updateHeaderConfig = async (id, data) => {
  const response = await api.put(`/header-configs/${id}`, data);
  return response.data;
};

export const deleteHeaderConfig = async (id) => {
  const response = await api.delete(`/header-configs/${id}`);
  return response.data;
};

// ─── Form Screens ──────────────────────────────────────────────────────────
// Every route here is app-scoped. `version_id` is a workflow version UID, and
// a UID is unique only per (uid, workflow_id) — a branch or clone of an app
// carries its parent's version UIDs — so without :appId the backend cannot
// tell which app's version is meant. It used to resolve the oldest match,
// which meant a BRANCH's form-designer save wrote the PARENT app's layout.
// Same convention (and the same resolveAppId safety net) as the version-scoped
// helpers in workflowApi.js.
export const fetchFormScreen = async (activityUid, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.get(`/apps/${resolveAppId(appId)}/form-screens/${activityUid}`, { params });
  return response.data;
};

export const upsertFormScreen = async (activityUid, data, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.put(`/apps/${resolveAppId(appId)}/form-screens/${activityUid}`, data, { params });
  return response.data;
};

export const deleteFormScreen = async (activityUid, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.delete(`/apps/${resolveAppId(appId)}/form-screens/${activityUid}`, { params });
  return response.data;
};

// ─── Grid Layouts ──────────────────────────────────────────────────────────
// Response carries `layouts` (per-device grid item arrays), `layout_configs`
// (per-device section config: {version,mode,sections}) and `bulk_grid_config`.
export const fetchGridLayout = async (activityUid, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.get(`/apps/${resolveAppId(appId)}/grid-layouts/${activityUid}`, { params });
  return response.data;
};

// data = { layouts: {desktop,tablet,mobile}, layout_configs: {desktop,tablet,mobile} }
// Always send BOTH keys: the backend treats a missing layout_configs entry as
// "no sections" and writes single mode for that device.
export const upsertGridLayout = async (activityUid, data, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.put(`/apps/${resolveAppId(appId)}/grid-layouts/${activityUid}`, data, { params });
  return response.data;
};

// Mass Activity "Separate forms" grid arrangement (order + width). Device-agnostic;
// the backend writes it across every device row. bulkGridConfig = [{ field_uid, width }].
// (fetchGridLayout's response already includes bulk_grid_config for loading.)
export const upsertBulkGridConfig = async (activityUid, bulkGridConfig, versionId, { appId } = {}) => {
  const params = versionId ? { version_id: versionId } : {};
  const response = await api.put(`/apps/${resolveAppId(appId)}/grid-layouts/${activityUid}/bulk-grid`, { bulk_grid_config: bulkGridConfig }, { params });
  return response.data;
};
